import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../../components/buttons/Button";
import Breadcrumbs from "../../../components/BreadCrumbs";
import StyledHeadingText from "../../../components/Fonts/StyledHeadingText";
import { toast } from "react-toastify";
import CustomLoading from "../../../components/customLoading/CustomLoading";
import ShowModal from "../../../components/Hud/ShowModal";
import { getHudPatientDoses } from "../../../services/hud/hud_doses";
import DoseTable from "../../../components/Hud/DoseTable";
import AddUpdateDose from "./add_update_dose";
import { pricesColorCoding } from "../../../constants/prices_colors_coding";

const PatientDoses = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const timerRef = useRef(null);

    const [patientRowData, setPatientRowData] = useState({});
    const [packageRowData, setPackageRowData] = useState({});
    const [tableData, setTableData] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingBg, setLoadingBg] = useState(false);

    // Fetch data function
    const fetchData = async (patientId, needLoading = true) => {
        if (needLoading) {
            setLoading(true);
        } else {
            setLoadingBg(true);
        }

        try {
            let res = await getHudPatientDoses(patientId);
            if (res.status === 200) {
                setTableData(res.data);
            } else {
                toast.error("Unable to get packages");
            }
        } catch (error) {
            console.error("Error fetching doses:", error);
        } finally {
            setLoading(false);
            setLoadingBg(false);
        }
    };

    // Function to check the status of the last entry in tableData
    const checkLastEntryStatus = () => {
        if (tableData && tableData.results && tableData.results.length > 0) {
            const lastEntry = tableData.results[tableData.results.length - 1];

            if (lastEntry.status === "Completed") {
                toast.success("Last dose status is Completed. Stopping updates.");
                if (timerRef.current) {
                    clearInterval(timerRef.current); // Stop the timer
                    timerRef.current = null;
                }
                return true; // Status is completed
            } else {
                toast.info("Last dose is still in progress.");
                return false; // Status is not completed
            }
        }
        return false; // No data available to check
    };

    // Start periodic updates
    const startTimer = (patientId) => {
        // Clear any existing timer
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        fetchData(patientId);
        timerRef.current = setInterval(() => {
            fetchData(patientId, false);
            checkLastEntryStatus();
        }, 15000);
    };

    // Stop the timer
    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    // UseEffect to initialize data
    useEffect(() => {
        const data = location.state;
        if (data) {
            setPatientRowData(data.patient);
            setPackageRowData(data.package);
            startTimer(data.package.package_id); // Start periodic updates
        } else {
            toast.error("Error while looking for package doses");
            navigate(-1);
        }

        return () => {
            stopTimer(); // Cleanup timer on component unmount
        };
    }, [location.state]);

    const [modalIsOpenPackage, setIsOpenPackage] = useState(false);

    const closeAddPackageModal = () => {
        setIsOpenPackage(false);
    };

    const handlePackageOperation = () => {
        closeAddPackageModal();
        if (tableData.length > 14 && packageRowData.remaining_amount !== 0) {
            setTimeout(() => {
                toast.warning(`Please collect remaining amount of ₹${packageRowData.remaining_amount} for current package`);
            }, 1000);
        }
        fetchData(packageRowData.package_id);
    };

    const openModalAddPackage = () => {
        setIsOpenPackage(true);
    };

    return loading ? (
        <CustomLoading />
    ) : (
        <div className="p-6 bg-white rounded-lg shadow-md">
            <div className="flex flex-row justify-between">
                <Breadcrumbs
                    items={[
                        { name: "Home", link: "/invoice" },
                        { name: "Patients", link: "/hud-patients" },
                        { name: "Patient Packages" },
                        { name: "Doses" },
                    ]}
                />
                {
                    loadingBg ? <p className="text-primary font-bold">Syncing...</p> : <></>
                }
            </div>
            <div className="w-full flex-1 overflow-x-auto">
                <div className="flex flex-row justify-between items-center my-2">
                    <StyledHeadingText
                        text={`${patientRowData.full_name} (${"Age: " +
                            patientRowData.age ||
                            "DOB: " + patientRowData.date_of_birth
                            } | Gen: ${patientRowData.gender
                            }) || (${"Package: " + packageRowData.package_name
                            } | ${"Type: " + packageRowData.package_status})`}
                    />
                    <Button
                        type="button"
                        className="bg-primary text-white px-4 py-2 rounded text-xs"
                        onClick={() => openModalAddPackage()}
                        disabled={loading}
                    >
                        {loading ? "Adding..." : "Add Dose"}
                    </Button>
                </div>
                <div className="flex flex-row items-center gap-2 mb-2">
                    <p className="font-bold text-xs">Pkg. Color: {pricesColorCoding.find((e) => e.amount === packageRowData.amount)?.color ?? `₹${packageRowData.amount}`}</p> <span className={`w-6 h-3 rounded-full border shadow ${pricesColorCoding.find((e) => e.amount === packageRowData.amount)?.color ? "" : "hidden"} ${pricesColorCoding.find((e) => e.amount === packageRowData.amount)?.bg ?? ""}`}></span> <p className="font-bold text-xs">{pricesColorCoding.find((e) => e.amount === packageRowData.amount)?.short_hand ?? ``}</p>
                    <p className="font-bold text-xs"> | Rem. Color: {pricesColorCoding.find((e) => e.amount === packageRowData.remaining_amount)?.color ?? `₹${packageRowData.remaining_amount}`}</p> <span className={`w-6 h-3 rounded-full border shadow ${pricesColorCoding.find((e) => e.amount === packageRowData.remaining_amount)?.color ? "" : "hidden"} ${pricesColorCoding.find((e) => e.amount === packageRowData.remaining_amount)?.bg ?? ""}`}></span> <p className="font-bold text-xs">{pricesColorCoding.find((e) => e.amount === packageRowData.remaining_amount)?.short_hand ?? ``}</p>
                </div>
                {loading ? (
                    <div className="flex w-full flex-row justify-center my-2">
                        <CustomLoading size={100} />
                    </div>
                ) : (
                    <DoseTable
                        data={tableData}
                        packageId={packageRowData.package_id}
                        fetchData={() =>
                            fetchData(packageRowData.package_id)
                        }
                    />
                )}
            </div>

            <div className="flex gap-4 justify-end w-full mt-4">
                <Button
                    type="button"
                    className="bg-gray-500 text-white px-4 py-2 rounded"
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>
            </div>
            <ShowModal
                contentLabel={"Add dose alert"}
                isOpen={modalIsOpenPackage}
                modalWidth={"50%"}
                onRequestClose={closeAddPackageModal}
                componentToPass={
                    <AddUpdateDose
                        packageId={packageRowData.package_id}
                        onDoseOperation={handlePackageOperation}
                    />
                }
            />
        </div>
    );
};

export default PatientDoses;
